import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { RequireAccess } from '../auth/RequireAccess';
import type { ConsentType } from '../dataProvider/types';
import { useConsent } from './useConsent';
import { RecordWithdrawalAction } from './RecordWithdrawalAction';
import type { ConsentCurrentRow, ConsentView } from './consentModel';
import {
  CONSENT_ACTION_COLOR,
  CONSENT_ACTION_LABEL,
  CONSENT_SOURCE_LABEL,
  CONSENT_TYPE_LABEL,
} from './consentState';

/**
 * Consent records (D-consent-records, Phase 7 surface 2). A per-member assembled-facts READ
 * view of a member's consent: the derived STANDING consent per type (latest record wins,
 * computed on read, never stored) AND the full append-only history. This is a read / audit
 * surface, NOT a worklist (no deadline, no urgency dimension), closer to the audit-log view.
 *
 * The surface is READ-ONLY except the one narrow consent:record_withdrawal write (recording a
 * withdrawal received off-app), which appends a compliance_logged revoke to the same history.
 *
 * OVERSIGHT-GLOBAL: unlike the operational per-member surfaces (tracking diagnostics, view as
 * user) it does NOT apply the switcher region guard. Compliance is cross-region accountability,
 * so a compliance officer reads any member's consent regardless of their active region (the DSAR
 * and audit-log posture). ROLE-gated to compliance and super_admin (a capability read-gate would
 * let the viewer's '*:read' wildcard in).
 *
 * POLICY VERSION: the surface shows the version IDENTIFIER a record was made against, NEVER the
 * policy text. The real version registry is a cross-repo seam; this does not claim to render the
 * policy itself.
 */

const day = (s: string): string => s.slice(0, 10);
const minute = (s: string): string => s.slice(0, 16).replace('T', ' ');

const STATUS_LABEL: Record<ConsentCurrentRow['status'], string> = {
  granted: 'Granted',
  not_granted: 'Not granted',
  unknown: 'No record',
};
const STATUS_COLOR: Record<
  ConsentCurrentRow['status'],
  'success' | 'default' | 'warning'
> = {
  granted: 'success',
  not_granted: 'default',
  unknown: 'warning',
};

const CurrentConsent = ({ view }: { view: ConsentView }) => (
  <Box sx={{ mb: 3 }} data-testid="consent-current">
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      Standing consent (derived from the latest record per type)
    </Typography>
    <Stack spacing={1}>
      {view.current.map((row) => (
        <Stack
          key={row.consentType}
          direction="row"
          spacing={1.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          data-testid={`consent-current-${row.consentType}`}
        >
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {CONSENT_TYPE_LABEL[row.consentType]}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="filled"
            color={STATUS_COLOR[row.status]}
            label={STATUS_LABEL[row.status]}
            data-testid={`consent-status-${row.consentType}`}
          />
          <Typography variant="caption" color="text.secondary">
            {row.since
              ? `since ${day(row.since)} | ${CONSENT_SOURCE_LABEL[row.source!]} | policy ${row.policyVersion}`
              : 'no record held for this consent'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

const HistoryTrail = ({ view }: { view: ConsentView }) => (
  <Box data-testid="consent-history">
    <Typography variant="subtitle2" sx={{ mb: 1 }}>
      Consent history ({view.history.length})
    </Typography>
    {view.history.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No consent records held for this member.
      </Typography>
    ) : (
      <Stack spacing={0.75}>
        {view.history.map((row) => (
          <Box
            key={row.id}
            data-testid={`consent-record-${row.id}`}
            sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {CONSENT_TYPE_LABEL[row.consentType]}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={CONSENT_ACTION_COLOR[row.action]}
                label={CONSENT_ACTION_LABEL[row.action]}
              />
              <Typography variant="caption" color="text.secondary">
                {CONSENT_SOURCE_LABEL[row.source]} | policy {row.policyVersion}{' '}
                | {minute(row.occurredAt)}
              </Typography>
            </Stack>
            {row.note && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                data-testid={`consent-note-${row.id}`}
              >
                Note: {row.note}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    )}
  </Box>
);

/** The surface without the access wrapper. Exported so tests can inject `asOf` (the clock the
 *  one withdrawal write stamps its record with). */
export const ConsentRecordsView = ({ asOf }: { asOf?: number }) => {
  const { userId } = useParams();
  const subjectId = Number(userId);
  const { role } = useAccessContext();
  const [now] = useState<number>(() => asOf ?? Date.now());
  const [refreshKey, setRefreshKey] = useState(0);
  const { view, isLoading } = useConsent(subjectId, refreshKey);

  if (isLoading || !view) return <Loading />;

  const canRecord = hasAccess(
    { role, accessibleRegions: [] },
    { capability: 'consent:record_withdrawal' },
  );
  const policyVersionFor = (type: ConsentType): string =>
    view.current.find((c) => c.consentType === type)?.policyVersion ??
    'unspecified';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Compliance
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Consent records
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="consent-member"
      >
        {view.memberName} | {view.memberEmail} | {view.market.toUpperCase()}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mb: 2 }}
        data-testid="consent-caption"
      >
        The provable record of this member's consent grants and revocations. The
        standing consent is derived from the latest record for each type, never
        a stored flag. The policy column shows the version identifier the record
        was made against, not the policy text.
      </Typography>

      {canRecord && (
        <Box sx={{ mb: 2 }}>
          <RecordWithdrawalAction
            user={{
              id: view.subjectUserId,
              name: view.memberName,
              market: view.market,
            }}
            now={now}
            policyVersionFor={policyVersionFor}
            onRecorded={() => setRefreshKey((k) => k + 1)}
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />
      <CurrentConsent view={view} />
      <Divider sx={{ mb: 2 }} />
      <HistoryTrail view={view} />
    </Box>
  );
};

/**
 * Gated entry registered at /consent/:userId. ROLE-gated to compliance and super_admin: a
 * viewer's read-everywhere '*:read' wildcard would slip through a capability read-gate, so the
 * explicit role gate is what holds the boundary (D-consent-records).
 */
export const ConsentRecords = () => (
  <RequireAccess roles={['compliance', 'super_admin']}>
    <ConsentRecordsView />
  </RequireAccess>
);
